import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Wrench, Award, Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, PRODUCTS, HERO_BG } from "../data/site";
import BrandStrip from "../components/BrandStrip";
import ProductCard from "../components/ProductCard";

const STATS = [
  { v: `${COMPANY.yearsExperience}+`, l: "Years of Industrial Service" },
  { v: `${COMPANY.productLines}+`, l: "Product Lines" },
  { v: `${COMPANY.citiesServed}+`, l: "Cities Served" },
  { v: "100%", l: "Genuine Parts" },
];

const PILLARS = [
  { icon: ShieldCheck, t: "Genuine, Traceable Parts", d: "Sourced from authorised stockists. SKF, FAG, NTN, Fenner & more — every batch traceable." },
  { icon: Truck, t: "Same-Day Dispatch", d: "Strong stock of fast-moving belts, bearings & seals. Pan-India transport tie-ups." },
  { icon: Wrench, t: "Crusher & Mining Specialists", d: "Deep expertise in jaw, cone, VSI, screen and conveyor spares for quarry plants." },
  { icon: Award, t: "Trusted by South India", d: "Decades of industrial supply across Karnataka, Andhra, Telangana, Tamil Nadu, Goa & Maharashtra." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] overflow-hidden bg-[#0A192F]">
        <img
          src={HERO_BG}
          alt="Industrial plant"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 h-full flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-6 fade-up">
            <span className="slash-divider"></span>
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#0EA5E9]">
              Davangere · Since the foundations of Indian industry
            </span>
          </div>
          <h1 className="fade-up font-head font-black text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Power transmission & crusher spares,
            <span className="block text-[#0EA5E9]">delivered with precision.</span>
          </h1>
          <p className="fade-up mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
            Conveyor belts, bearings, gear boxes, motors, VSI spares and a complete range of
            industrial essentials — under one trusted roof in Davangere.
          </p>
          <div className="fade-up mt-10 flex flex-wrap gap-4">
            <Link
              to="/products"
              data-testid="hero-cta-products"
              className="inline-flex items-center gap-2 bg-[#0EA5E9] text-white px-7 py-4 font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#0284C7] transition-colors"
            >
              Browse Products
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/enquiry"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-7 py-4 font-bold uppercase tracking-[0.18em] text-xs hover:bg-white hover:text-[#0A192F] transition-colors"
            >
              Request a Quote
            </Link>
          </div>

          {/* Stats */}
          <div className="absolute left-4 right-4 lg:left-8 lg:right-8 bottom-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {STATS.map((s) => (
              <div key={s.l} className="bg-[#0A192F]/80 backdrop-blur p-5">
                <div className="font-head font-black text-white text-3xl md:text-4xl tracking-tight">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandStrip />

      {/* ABOUT */}
      <section id="about" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="slash-divider"></span>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">About Us</span>
            </div>
            <h2 className="font-head font-black text-[#0A192F] text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Built on technical depth, run on trust.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[#475569] text-[17px] leading-relaxed">
            <p>
              <strong className="text-[#0A192F]">Kashetter Industrial Essentials</strong> is a
              Davangere-based supplier of mechanical power-transmission products and crusher
              wear-parts. Run by proprietor <strong className="text-[#0A192F]">{COMPANY.proprietor}</strong>,
              we have grown into a one-stop source for stone-crusher, mining and process-plant
              maintenance teams across Karnataka and beyond.
            </p>
            <p>
              From rubber conveyor belts and idler rollers to bearings (SKF, FAG, NTN, Timken, NBC),
              V-belts (Fenner, PIX, KIE-Powerdrive), SMSR &amp; helical gear boxes, motors from 1 HP
              to 200 HP, jaw plates, cone mantles, VSI spares, vibro motors, oil seals, gaskets,
              couplings, hanging magnets and trolley wheels — if your plant needs it, we stock it.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4">
              <div className="border-l-2 border-[#0EA5E9] pl-3">
                <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">Established</div>
                <div className="font-head font-bold text-[#0A192F]">South India</div>
              </div>
              <div className="border-l-2 border-[#0EA5E9] pl-3">
                <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">Proprietor</div>
                <div className="font-head font-bold text-[#0A192F]">{COMPANY.proprietor}</div>
              </div>
              <div className="border-l-2 border-[#0EA5E9] pl-3">
                <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">HQ</div>
                <div className="font-head font-bold text-[#0A192F]">Davangere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p) => (
            <div key={p.t} className="bg-white p-7 border border-[#E2E8F0] hover:border-[#0EA5E9] transition-colors" data-testid={`pillar-${p.t.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 bg-[#0A192F] text-[#0EA5E9] flex items-center justify-center mb-4">
                <p.icon size={22} />
              </div>
              <h3 className="font-head font-bold text-[#0A192F] text-lg">{p.t}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section id="products" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="slash-divider"></span>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">Our Range</span>
              </div>
              <h2 className="font-head font-black text-[#0A192F] text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Engineered for heavy industry.
              </h2>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:text-[#0EA5E9]"
              data-testid="home-view-all-products"
            >
              View All Products <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.slice(0, 9).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[#0A192F] text-white py-20 grain">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <h2 className="font-head font-black text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Need a fast quote on a critical spare?
            </h2>
            <p className="mt-4 text-white/70 max-w-xl text-lg">
              Share your part number, drawing or crusher model. We&rsquo;ll respond with stock,
              specs and best price the same working day.
            </p>
          </div>
          <div className="md:col-span-5 grid sm:grid-cols-2 gap-4">
            <a href={COMPANY.phoneHref} className="bg-white/5 hover:bg-white/10 border border-white/10 p-5 transition-colors" data-testid="cta-call">
              <Phone size={18} className="text-[#0EA5E9]" />
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-3">Call</div>
              <div className="font-head font-bold text-lg">{COMPANY.phone}</div>
            </a>
            <a href={COMPANY.emailHref} className="bg-white/5 hover:bg-white/10 border border-white/10 p-5 transition-colors" data-testid="cta-email">
              <Mail size={18} className="text-[#0EA5E9]" />
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-3">Email</div>
              <div className="font-head font-bold text-lg break-all">{COMPANY.email}</div>
            </a>
            <Link to="/enquiry" className="sm:col-span-2 bg-[#0EA5E9] hover:bg-[#0284C7] p-5 transition-colors" data-testid="cta-enquiry">
              <MapPin size={18} />
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/80 mt-3">Send Enquiry</div>
              <div className="font-head font-bold text-lg">Get a tailored quote →</div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
